import { supabase } from '@/integrations/supabase/client';

export interface BackupConfiguration {
  id: string;
  name: string;
  description?: string;
  backup_type: 'full' | 'incremental' | 'differential';
  schedule_cron?: string;
  retention_days: number;
  storage_config: any;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface BackupJob {
  id: string;
  configuration_id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  backup_type: string;
  started_at?: string;
  completed_at?: string;
  file_size_bytes?: number;
  file_path?: string;
  error_message?: string;
  metadata: any;
  created_at: string;
}

export interface RestoreJob {
  id: string;
  backup_job_id: string;
  target_environment: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at?: string;
  completed_at?: string;
  restored_tables?: string[];
  error_message?: string;
  metadata: any;
  created_by?: string;
  created_at: string;
}

export interface BackupVerification {
  id: string;
  backup_job_id: string;
  verification_type: 'checksum' | 'restore_test' | 'data_integrity';
  status: 'pending' | 'passed' | 'failed';
  verification_data: any;
  error_message?: string;
  verified_at?: string;
  created_at: string;
}

export class BackupService {
  
  /**
   * Get all backup configurations
   */
  static async getBackupConfigurations(): Promise<BackupConfiguration[]> {
    try {
      const { data, error } = await supabase
        .from('backup_configurations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching backup configurations:', error);
        return [];
      }

      return (data || []) as BackupConfiguration[];
    } catch (error) {
      console.error('Error in getBackupConfigurations:', error);
      return [];
    }
  }

  /**
   * Create a new backup configuration
   */
  static async createBackupConfiguration(config: {
    name: string;
    description?: string;
    backup_type: string;
    schedule_cron?: string;
    retention_days: number;
    storage_config: any;
  }): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('backup_configurations')
        .insert({
          name: config.name,
          description: config.description,
          backup_type: config.backup_type,
          schedule_cron: config.schedule_cron,
          retention_days: config.retention_days,
          storage_config: config.storage_config,
          is_active: true
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating backup configuration:', error);
        return null;
      }

      return data?.id || null;
    } catch (error) {
      console.error('Error in createBackupConfiguration:', error);
      return null;
    }
  }

  /**
   * Update backup configuration status
   */
  static async updateBackupConfigurationStatus(configId: string, isActive: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('backup_configurations')
        .update({ 
          is_active: isActive,
          updated_at: new Date().toISOString()
        })
        .eq('id', configId);

      if (error) {
        console.error('Error updating backup configuration status:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in updateBackupConfigurationStatus:', error);
      return false;
    }
  }

  /**
   * Get backup jobs
   */
  static async getBackupJobs(configId?: string): Promise<BackupJob[]> {
    try {
      let query = supabase
        .from('backup_jobs')
        .select(`
          *,
          backup_configurations!inner(name)
        `)
        .order('created_at', { ascending: false });

      if (configId) {
        query = query.eq('configuration_id', configId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching backup jobs:', error);
        return [];
      }

      return (data || []) as BackupJob[];
    } catch (error) {
      console.error('Error in getBackupJobs:', error);
      return [];
    }
  }

  /**
   * Get restore jobs
   */
  static async getRestoreJobs(): Promise<RestoreJob[]> {
    try {
      const { data, error } = await supabase
        .from('restore_jobs')
        .select(`
          *,
          backup_jobs!inner(
            backup_type,
            backup_configurations!inner(name)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching restore jobs:', error);
        return [];
      }

      return (data || []) as RestoreJob[];
    } catch (error) {
      console.error('Error in getRestoreJobs:', error);
      return [];
    }
  }

  /**
   * Get backup verifications
   */
  static async getBackupVerifications(backupJobId?: string): Promise<BackupVerification[]> {
    try {
      let query = supabase
        .from('backup_verifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (backupJobId) {
        query = query.eq('backup_job_id', backupJobId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching backup verifications:', error);
        return [];
      }

      return (data || []) as BackupVerification[];
    } catch (error) {
      console.error('Error in getBackupVerifications:', error);
      return [];
    }
  }

  /**
   * Create backup manually
   */
  static async createBackup(configId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('backup-manager', {
        body: { 
          action: 'create_backup',
          configId: configId
        }
      });

      if (error) {
        console.error('Error creating backup:', error);
        return false;
      }

      return data?.success || false;
    } catch (error) {
      console.error('Error in createBackup:', error);
      return false;
    }
  }

  /**
   * Restore from backup
   */
  static async restoreBackup(
    backupJobId: string, 
    targetEnvironment: string = 'staging',
    selectedTables: string[] = []
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('backup-manager', {
        body: { 
          action: 'restore_backup',
          restoreData: {
            backupJobId,
            targetEnvironment,
            selectedTables
          }
        }
      });

      if (error) {
        console.error('Error restoring backup:', error);
        return false;
      }

      return data?.success || false;
    } catch (error) {
      console.error('Error in restoreBackup:', error);
      return false;
    }
  }

  /**
   * Verify backup
   */
  static async verifyBackup(backupJobId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.functions.invoke('backup-manager', {
        body: { 
          action: 'verify_backup',
          jobId: backupJobId
        }
      });

      if (error) {
        console.error('Error verifying backup:', error);
        return false;
      }

      return data?.success || false;
    } catch (error) {
      console.error('Error in verifyBackup:', error);
      return false;
    }
  }

  /**
   * Schedule backup using database function
   */
  static async scheduleBackup(configId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.rpc('schedule_backup_job', {
        config_id: configId
      });

      if (error) {
        console.error('Error scheduling backup:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in scheduleBackup:', error);
      return null;
    }
  }

  /**
   * Get backup statistics
   */
  static async getBackupStats(): Promise<{
    total_configs: number;
    active_configs: number;
    total_backups: number;
    successful_backups: number;
    failed_backups: number;
    storage_used: number;
    last_backup_time?: string;
  }> {
    try {
      const [configsRes, jobsRes, storageRes] = await Promise.all([
        supabase
          .from('backup_configurations')
          .select('id, is_active', { count: 'exact' }),
        
        supabase
          .from('backup_jobs')
          .select('status, file_size_bytes, completed_at', { count: 'exact' }),
        
        supabase
          .from('backup_jobs')
          .select('file_size_bytes')
          .eq('status', 'completed')
      ]);

      const totalConfigs = configsRes.count || 0;
      const activeConfigs = configsRes.data?.filter(c => c.is_active).length || 0;
      const totalBackups = jobsRes.count || 0;
      const successfulBackups = jobsRes.data?.filter(j => j.status === 'completed').length || 0;
      const failedBackups = jobsRes.data?.filter(j => j.status === 'failed').length || 0;
      
      const storageUsed = storageRes.data?.reduce((total, job) => {
        return total + (job.file_size_bytes || 0);
      }, 0) || 0;

      const lastBackupTime = jobsRes.data
        ?.filter(j => j.status === 'completed' && j.completed_at)
        ?.sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())
        ?.[0]?.completed_at;

      return {
        total_configs: totalConfigs,
        active_configs: activeConfigs,
        total_backups: totalBackups,
        successful_backups: successfulBackups,
        failed_backups: failedBackups,
        storage_used: storageUsed,
        last_backup_time: lastBackupTime
      };
    } catch (error) {
      console.error('Error in getBackupStats:', error);
      return {
        total_configs: 0,
        active_configs: 0,
        total_backups: 0,
        successful_backups: 0,
        failed_backups: 0,
        storage_used: 0
      };
    }
  }
}